<template>
  <el-dialog
    title="Edit photo"
    :visible.sync="syncedVisible"
    :close-on-click-modal="false"
    width="520px"
    @close="reset"
  >
    <div class="op-edit" v-if="image">
      <img :src="image.url" :alt="image.id" class="op-edit__preview"/>

      <label class="op-edit__field">
        <span>Operation</span>
        <el-select
          v-model="form.operation_id"
          placeholder="Select operation"
          filterable
          no-data-text="No data"
          no-match-text="No match"
        >
          <el-option
            v-for="op in operations"
            :key="op.id"
            :label="op.name && op.name.en ? op.name.en : op.id"
            :value="op.id"
          />
        </el-select>
      </label>

      <label class="op-edit__field">
        <span>Year</span>
        <el-input-number v-model="form.year" :min="1900" :max="2100" :controls="false"/>
      </label>

      <label class="op-edit__field">
        <span>Patient age</span>
        <el-input-number v-model="form.patient_age" :min="0" :max="150" :controls="false"/>
      </label>

      <label class="op-edit__field">
        <span>Doctor ID</span>
        <el-input-number v-model="form.doctor_id" :min="1" :controls="false"/>
      </label>
    </div>

    <div slot="footer" class="op-edit__footer">
      <base-button type="danger" :disabled="busy" @click="onDelete">Delete</base-button>
      <div class="op-edit__footer-right">
        <base-button type="default" @click="close">Cancel</base-button>
        <base-button type="primary" :disabled="busy" @click="save">
          {{ busy ? 'Saving...' : 'Save' }}
        </base-button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { mapActions } from 'vuex';

const emptyForm = () => ({
  operation_id: null,
  year: null,
  patient_age: null,
  doctor_id: null,
});

export default {
  props: {
    visible: { type: Boolean, required: true },
    image: { type: Object, default: null },
    operations: { type: Array, default: () => [] },
  },
  data: () => ({
    form: emptyForm(),
    busy: false,
  }),
  computed: {
    syncedVisible: {
      get() {
        return this.visible;
      },
      set(v) {
        this.$emit('update:visible', v);
      },
    },
  },
  watch: {
    image: {
      immediate: true,
      handler(img) {
        if (!img) {
          this.form = emptyForm();
          return;
        }
        this.form = {
          operation_id: img.operation_id || null,
          year: img.year || null,
          patient_age: img.patient_age || null,
          doctor_id: img.doctor_id || null,
        };
      },
    },
  },
  methods: {
    ...mapActions('operationResultImages', ['update', 'remove']),
    close() {
      this.syncedVisible = false;
    },
    reset() {
      this.busy = false;
    },
    async save() {
      if (!this.image) return;
      this.busy = true;
      try {
        const data = {};
        ['operation_id', 'year', 'patient_age', 'doctor_id'].forEach((k) => {
          if (this.form[k] !== null && this.form[k] !== '') data[k] = this.form[k];
        });
        await this.update({ id: this.image.id, data });
        this.$notify({ type: 'success', message: 'Saved' });
        this.$emit('saved');
        this.close();
      } catch (e) {
        this.$notify({ type: 'danger', message: e?.response?.data?.error || 'Save failed' });
      } finally {
        this.busy = false;
      }
    },
    async onDelete() {
      if (!this.image) return;
      if (!confirm('Delete this photo?')) return;
      this.busy = true;
      try {
        await this.remove(this.image.id);
        this.$notify({ type: 'success', message: 'Deleted' });
        this.$emit('deleted');
        this.close();
      } catch (e) {
        this.$notify({ type: 'danger', message: e?.response?.data?.error || 'Delete failed' });
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.op-edit {
  display: flex;
  flex-direction: column;
  gap: 14px;
  &__preview {
    width: 100%;
    max-height: 260px;
    object-fit: contain;
    background: #f5f5f5;
    border-radius: 6px;
  }
  &__field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    span {
      font-size: 13px;
      color: #555;
    }
  }
  &__footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    &-right {
      display: flex;
      gap: 8px;
    }
  }
}
</style>
